<script setup>
import { useReferenceWorkspace } from "./referenceWorkspace";
import ReferenceResources from "@/components/ReferenceResources.vue";
import * as Icon from "@heroicons/vue/24/solid";
const {
  auth,
  reference,
  resourceEditorRef,
  sectionSaving,
  sectionEditButtonClasses,
  sectionDoneButtonClasses,
  resourceConflict,
  resourceDraftSavedAt,
  resourceDraftRecovery,
  setScheduleDateDraftsDirty,
  scheduleEditing,
  canManageSchedule,
  isOnline,
  resourceCollaborators,
  setSectionRef,
  scheduleSummary,
  isResourceDirty,
  openScheduleManager,
  closeScheduleManager,
  resourceSaveStatus,
  resourceSaveStatusTone,
  restoreResourceDraft,
  discardRecoveredResourceDraft,
  reloadResourceAfterConflict,
  saveSection,
} = useReferenceWorkspace();
</script>

<template>
  <section
    v-if="canManageSchedule"
    id="schedule"
    :ref="(el) => setSectionRef('schedule', el)"
    class="min-w-0 scroll-mt-24 2xl:col-span-12"
  >
    <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div
          class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
        >
          Schedule
        </div>
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Schedule management
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Manage dates, shift times and locations separately from resource
          allocation.
        </p>
      </div>
      <button
        v-if="!scheduleEditing"
        type="button"
        :class="sectionEditButtonClasses"
        @click.prevent="openScheduleManager"
      >
        <Icon.PencilSquareIcon class="mr-1 inline h-4 w-4" /> Edit schedule
      </button>
      <span
        v-else
        class="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
      >
        Editing schedule
      </span>
    </div>

    <div
      v-if="!scheduleEditing"
      class="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:grid-cols-2 xl:grid-cols-4"
    >
      <div class="sm:col-span-2 xl:col-span-1">
        <div
          class="text-xs font-semibold uppercase tracking-wide text-slate-400"
        >
          Scheduled dates
        </div>
        <div class="mt-1 font-semibold text-slate-900 dark:text-slate-100">
          {{ scheduleSummary.dateRange }}
        </div>
      </div>
      <div>
        <div
          class="text-xs font-semibold uppercase tracking-wide text-slate-400"
        >
          Days
        </div>
        <div
          class="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100"
        >
          {{ scheduleSummary.dayCount }}
        </div>
      </div>
      <div>
        <div
          class="text-xs font-semibold uppercase tracking-wide text-slate-400"
        >
          Shifts
        </div>
        <div
          class="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100"
        >
          {{ scheduleSummary.shiftCount }}
        </div>
      </div>
      <div>
        <div
          class="text-xs font-semibold uppercase tracking-wide text-slate-400"
        >
          Locations
        </div>
        <div
          class="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100"
        >
          {{ scheduleSummary.locationCount }}
        </div>
      </div>
    </div>

    <div
      v-if="scheduleEditing && resourceDraftRecovery"
      class="mb-4 rounded-xl border border-violet-300 bg-violet-50 p-4 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100"
    >
      <div class="font-semibold">
        {{
          resourceDraftRecovery.revisionMismatch
            ? "A protected draft exists for an older schedule"
            : "Protected schedule changes were recovered"
        }}
      </div>
      <p class="mt-1 text-xs opacity-80">
        <template v-if="resourceDraftRecovery.revisionMismatch">
          The server changed after this draft was created. You can load it
          locally to review, but saving it will require explicit confirmation.
        </template>
        <template v-else>
          Draft saved
          {{ new Date(resourceDraftRecovery.saved_at).toLocaleString() }}.
          Restore it to resume automatic saving to the job.
        </template>
      </p>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-lg bg-violet-700 px-3 py-2 text-xs font-semibold text-white"
          @click="restoreResourceDraft"
        >
          {{
            resourceDraftRecovery.revisionMismatch
              ? "Review recovered draft"
              : "Restore draft"
          }}
        </button>
        <button
          type="button"
          class="rounded-lg border border-violet-300 px-3 py-2 text-xs font-semibold dark:border-violet-700"
          @click="discardRecoveredResourceDraft"
        >
          Discard draft
        </button>
      </div>
    </div>

    <ReferenceResources
      v-if="scheduleEditing"
      ref="resourceEditorRef"
      mode="schedule"
      embedded
      :reference="reference"
      :editable="true"
      @schedule-dirty="setScheduleDateDraftsDirty"
    >
      <template #footer>
        <div
          v-if="scheduleEditing"
          class="sticky bottom-3 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-sm"
          :class="{
            'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/95 dark:text-amber-100':
              resourceSaveStatusTone === 'dirty',
            'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/95 dark:text-sky-100':
              resourceSaveStatusTone === 'saving',
            'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/95 dark:text-red-100':
              resourceSaveStatusTone === 'error',
            'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/95 dark:text-emerald-100':
              resourceSaveStatusTone === 'saved',
          }"
          role="status"
        >
          <div>
            <div class="font-semibold">{{ resourceSaveStatus }}</div>
            <div
              v-if="resourceDraftSavedAt && isResourceDirty"
              class="mt-0.5 text-xs opacity-75"
            >
              Device draft updated
              {{
                resourceDraftSavedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }}
            </div>
            <div
              v-if="resourceCollaborators.length"
              class="mt-1 text-xs font-medium"
            >
              Also viewing:
              {{
                resourceCollaborators.map((person) => person.name).join(", ")
              }}
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-if="resourceConflict"
              type="button"
              class="rounded-lg border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-800 dark:border-red-700 dark:bg-slate-900 dark:text-red-200"
              @click="reloadResourceAfterConflict"
            >
              Reload latest
            </button>
            <button
              type="button"
              :class="sectionDoneButtonClasses"
              :disabled="sectionSaving.resources"
              @click.prevent="closeScheduleManager"
            >
              {{ isResourceDirty ? "Discard changes" : "Close" }}
            </button>
            <button
              type="button"
              class="btn save"
              :disabled="sectionSaving.resources || !isOnline"
              @click.prevent="saveSection('resources')"
            >
              <Icon.CheckIcon
                v-if="!sectionSaving.resources"
                class="mr-1 inline h-4 w-4"
              />{{ sectionSaving.resources ? "Saving..." : "Save schedule" }}
            </button>
          </div>
        </div>
      </template>
    </ReferenceResources>
  </section>
</template>
